// THE RANKED MOVES (portal-admin D13).
//
// Eight rules, every one a pure function of the projection payload — a boolean, a count or a
// timestamp read from a column. Nothing here is generated at render time, which is §0's
// determinism mandate: this page's output IS a claim about what a client can see, and a readout
// that guessed would be worse than none.
//
// SHAPE: each rule emits the shared `Move` (queueRules.ts, imported as a TYPE so nothing from
// src/server reaches the browser bundle), narrowed with the label of the act that clears it.
// The comparator is the shared one — weight ASC, then rule name — and the weights are this
// tab's own: the study ranked them, and declaration order here IS that ranking.
//
// Each move cites the column or query it read, in `refs`. The page dresses those as dashed
// chips: they are provenance, not links.

import type { Move } from '../../../server/utils/queueRules.js';
import type { Vars } from '../../../shared/i18n.js';
import type { Projection } from '../../api/portalAdmin.js';
import './ladder.de.js';
import './ladder.fr.js';

/** The five zone anchors a move can send the operator to. */
export type MoveAnchor = 'pa-z-move' | 'pa-z-proj' | 'pa-z-shared' | 'pa-z-people' | 'pa-z-talk';

export interface PortalMove extends Move {
  /** The verb on the control. The lead move's is the page's ONE filled button. */
  cta: string;
  anchor: MoveAnchor;
  /** The master switch is the act itself, not a scroll — the lead move performs it. */
  act?: 'enable-portal';
}

/** The translator and formatters the page hands in — this is a plain function, not a component. */
export interface MoveLocale {
  t: (key: string, vars?: Vars) => string;
  tp: (n: number, one: string, other: string, vars?: Vars) => string;
  fmtDate: (iso: string | null | undefined) => string;
}

const refList = (refs: string[], limit = 6): string =>
  refs.length <= limit ? refs.join(', ') : `${refs.slice(0, limit).join(', ')} +${refs.length - limit}`;

/**
 * The ladder. Order of declaration is the ranking; `weight` restates it so the shared
 * comparator (weight ASC → rule name) sorts identically wherever these are merged.
 */
export function portalMoves(p: Projection, loc: MoveLocale): PortalMove[] {
  const { t, tp, fmtDate } = loc;
  const day = (iso: string): string => fmtDate(iso.slice(0, 10));
  const out: PortalMove[] = [];
  const enabled = p.engagement.portal_enabled;
  const shared = p.shared.n;
  const accounts = p.people.accounts;
  const client = p.engagement.client_name;

  // 10 · The switch is off and there is a projection sitting behind it. Every portal call is
  //      refused before publication is even consulted, so an invite sent today hits a locked door.
  if (!enabled && (shared > 0 || accounts > 0)) {
    out.push({
      rule: 'portal-off-with-projection',
      weight: 10,
      tone: 'attend',
      title: t('The portal is switched off, and there is a projection behind it'),
      why: t('{rows} flagged published and {accounts}. With the master switch off every portal call is refused, so an invite sent today lands on a locked door.', {
        rows: tp(shared, '{n} row is', '{n} rows are'),
        accounts: tp(accounts, '{n} account exists', '{n} accounts exist'),
      }),
      route: { kind: 'in-page', anchor: 'pa-z-move' },
      refs: ['engagements.portal_enabled = false'],
      cta: t('Switch the portal on'),
      anchor: 'pa-z-move',
      act: 'enable-portal',
    });
  }

  // 20 · A draft statement in front of a client reads as a commitment.
  if (p.draft_published.length) {
    const refs = p.draft_published.map((r) => r.ref);
    const n = refs.length;
    out.push({
      rule: 'draft-shared',
      weight: 20,
      tone: 'attend',
      title: tp(n, '{n} draft statement is in front of the client', '{n} draft statements are in front of the client'),
      why: tp(n,
        '{refs} is still draft in the register and published to the portal. A draft statement shown to a client reads as a commitment.',
        '{refs} are still draft in the register and published to the portal. A draft statement shown to a client reads as a commitment.',
        { refs: refList(refs) }),
      route: { kind: 'in-page', anchor: 'pa-z-shared' },
      refs: ['status = draft', 'published = true'],
      cta: t('Stop sharing the drafts'),
      anchor: 'pa-z-shared',
    });
  }

  // 30 · Nothing is shared at all. The first decision is which object types the client may see.
  if (!enabled && shared === 0 && accounts === 0) {
    const reqs = p.counts.requirements.total;
    const docs = p.counts.documents.total;
    out.push({
      rule: 'nothing-shared',
      weight: 30,
      tone: 'ink',
      title: t('Nothing is shared with {client} yet', { client }),
      why: t('This engagement has {reqs} and {docs} on record, and none of them is visible to the client. The first decision is which object types the client may see at all.', {
        reqs: tp(reqs, '{n} requirement', '{n} requirements'),
        docs: tp(docs, '{n} document', '{n} documents'),
      }),
      route: { kind: 'in-page', anchor: 'pa-z-shared' },
      refs: ['publication_settings', 'engagements.portal_enabled = false'],
      cta: t('Choose what they may see'),
      anchor: 'pa-z-shared',
    });
  }

  // 40 · The client is reading the new text under the old decision.
  if (p.drift.length) {
    const refs = p.drift.map((d) => d.ref);
    const n = refs.length;
    const lastPublish = p.drift.reduce((a, d) => (d.published_at > a ? d.published_at : a), p.drift[0].published_at);
    const newestRevision = p.drift.reduce((a, d) => (d.revised_at > a ? d.revised_at : a), p.drift[0].revised_at);
    out.push({
      rule: 'revised-after-publish',
      weight: 40,
      tone: 'attend',
      title: tp(n, '{n} published statement changed after you published it', '{n} published statements changed after you published them'),
      why: t('{refs} — last publish pass {published}, current revision written {revised}. The client is reading the new text under the old decision.', {
        refs: refList(refs), published: day(lastPublish), revised: day(newestRevision),
      }),
      route: { kind: 'in-page', anchor: 'pa-z-shared' },
      refs: ['requirement_revisions.created_at > audit_log(portal.publish).created_at'],
      cta: t('Review what changed'),
      anchor: 'pa-z-shared',
    });
  }

  // 50 · A thread whose last word is the client's.
  if (p.awaiting.length) {
    const first = p.awaiting[0];
    const subject = first.ref ?? `${first.object_type.replace(/_/g, ' ')} #${first.object_id}`;
    const more = p.awaiting.length - 1;
    out.push({
      rule: 'comment-unanswered',
      weight: 50,
      tone: 'attend',
      title: first.who
        ? tp(first.days, '{who} asked a question {n} day ago', '{who} asked a question {n} days ago', { who: first.who })
        : tp(first.days, 'A question has been waiting {n} day', 'A question has been waiting {n} days'),
      why: t('On {subject}. Nothing has gone back.', { subject })
        + (more > 0 ? ` ${tp(more, '{n} more thread is waiting behind it.', '{n} more threads are waiting behind it.')}` : ''),
      route: { kind: 'in-page', anchor: 'pa-z-talk' },
      refs: ['portal_comments: last author is the client'],
      cta: t('Answer it'),
      anchor: 'pa-z-talk',
    });
  }

  // 60 · An empty tab in a client portal reads as an unfinished system.
  const empties = p.views.filter((v) => v.empty);
  if (empties.length) {
    out.push({
      rule: 'view-opens-empty',
      weight: 60,
      tone: 'attend',
      title: t('{views} would open empty', { views: empties.map((v) => t(v.label)).join(` ${t('and')} `) }),
      why: t('The object type is visible, so the tab renders in the client’s navigation, and there is nothing published behind it. An empty tab in a client portal reads as an unfinished system.'),
      route: { kind: 'in-page', anchor: 'pa-z-proj' },
      refs: empties.map((v) => `${v.needs}: ${v.visibility}, 0 published`),
      cta: t('Hide it or fill it'),
      anchor: 'pa-z-shared',
    });
  }

  // 70 · The portal is on and nobody can reach it.
  if (enabled && accounts === 0) {
    out.push({
      rule: 'nobody-can-sign-in',
      weight: 70,
      tone: 'attend',
      title: t('The portal is on and nobody can sign in'),
      why: t('No portal account exists for {client}. A portal account can be made from a contact already on the client record, without retyping a name or an address.', { client }),
      route: { kind: 'in-page', anchor: 'pa-z-people' },
      refs: ['portal_users: 0 rows granted this engagement'],
      cta: t('Invite from contacts'),
      anchor: 'pa-z-people',
    });
  }

  // 80 · Showing an exclusion states what you are NOT doing. That is a contractual sentence.
  if (p.published_exclusions.length) {
    const refs = p.published_exclusions.map((r) => r.ref);
    const n = refs.length;
    out.push({
      rule: 'exclusions-published',
      weight: 80,
      tone: 'attend',
      title: tp(n, '{n} exclusion is published', '{n} exclusions are published'),
      // The confirm-modal copy this replaces was the best sentence on the old page; it moves verbatim.
      why: t('{refs}. Showing an exclusion to a client is a contractual statement about what you are not doing — publish it only if you mean to make that point.', { refs: refList(refs) }),
      route: { kind: 'in-page', anchor: 'pa-z-shared' },
      refs: ["type = 'out_of_scope' or status = 'descoped'"],
      cta: t('Check them'),
      anchor: 'pa-z-shared',
    });
  }

  // The shared comparator: weight, then rule name. Array.prototype.sort is stable, so a tie
  // resolves to declaration order and the ranking is reproducible.
  return out.sort((a, b) => (a.weight - b.weight) || a.rule.localeCompare(b.rule));
}

/** What the zone says when no rule fires. Stated once, here, so the page cannot re-word it (the page wraps it in t()). */
export const ALL_CLEAR = 'The projection matches the decisions on record. Nothing here needs you.';
